import { AlertModal, Box, Button, Flex } from '@modusign/design-system';
import { useRef, useState } from 'react';

import uploadIcon from '@app/assets/icons/icon-upload.svg';
import LoadingProgressBar from '@app/components/Modal/UploadModal/LoadingProgressBar';
import UpLoadModalDropZone from '@app/components/Modal/UploadModal/UpLoadModalContent/UpLoadModalDropZone';
import {
  WindowModal,
  WindowModalContent,
  WindowModalFooter,
  WindowModalHeader,
} from '@app/components/Modal/WindowModal';
import { ACCEPT_FILETYPE, MAX_FILE_UPLOAD_BYTE } from '@app/constants/UpLoadModal';
import useTemplateDocumentFileUpload from '@app/hooks/documentSetup/useTemplateDocumentFileUpload';
import { usePDF } from '@app/hooks/usePDF';
import getExtensionFromFileName from '@app/libs/file/getExtensionFromFileName';
import UploadFile from '@app/store/documents/UploadFile';

export default function CreateTemplateLocalFileUploadModal() {
  const { pdf } = usePDF();

  const [error, setError] = useState<{ title: string; content: string } | null>(null);
  const { current: hadPdf } = useRef(pdf);
  const [file, setFile] = useState<File | UploadFile | null>(null);

  const { isLoading, mutate: uploadDocumentFile } = useTemplateDocumentFileUpload(
    'createTemplateFromEmbeddedDraft'
  );

  const handleOnSubmit = () => {
    if (file) {
      uploadDocumentFile(file);
    }
  };

  const handleOnDrop = (acceptFile: Array<File | UploadFile>) => {
    if (!acceptFile[0]) {
      setError({
        title: '선택되지 않은 파일',
        content: '파일이 선택되지 않았습니다.',
      });
      return;
    }
    const extension = acceptFile[0].name.split('.').pop()?.toLowerCase();

    if (!extension || !ACCEPT_FILETYPE.includes(extension)) {
      setError({
        title: '지원하지 않는 파일 형식',
        content: '해당 문서의 파일 형식은 지원되지 않습니다.',
      });
      return;
    }
    if (acceptFile[0].size > MAX_FILE_UPLOAD_BYTE) {
      setError({
        title: '업로드 용량 초과',
        content: '최대 업로드 가능 용량은 5MB입니다.',
      });
      return;
    }

    setFile(acceptFile[0]);
  };

  if (hadPdf) {
    return null;
  }

  return (
    <>
      <WindowModal isOpen={!pdf && !isLoading} maxWidth="sm">
        <WindowModalHeader>
          <Flex>
            <Box alt="upload-icon" as="img" mr={2} src={uploadIcon} />
            문서 업로드
          </Flex>
        </WindowModalHeader>
        <WindowModalContent height={400} px={8} py={0}>
          <UpLoadModalDropZone
            fileName={file?.name}
            uploadType={'LOCAL'}
            onDrop={handleOnDrop}
            onReset={() => setFile(null)}
          />
        </WindowModalContent>
        <WindowModalFooter>
          <Box textAlign="right">
            <Button disabled={!file} variant="primary" width="wide" onClick={handleOnSubmit}>
              업로드
            </Button>
          </Box>
        </WindowModalFooter>
      </WindowModal>

      {!pdf && (
        <LoadingProgressBar
          extension={file ? getExtensionFromFileName(file.name) : null}
          isLoading={isLoading}
        />
      )}
      <AlertModal
        isOpen={!!error}
        title={error?.title ?? ''}
        variant="warning"
        onClose={() => setError(null)}
      >
        {error?.content}
      </AlertModal>
    </>
  );
}
